if (!customElements.get('partnership-form')) {
  class PartnershipForm extends HTMLElement {
    connectedCallback() {
      this.host = this.closest('.gly-partnership');
      this.radios = Array.from(this.querySelectorAll('[data-track-input]'));
      this.fieldGroups = Array.from(this.querySelectorAll('.gly-partnership__field-group[data-track]'));
      this.formStep = this.querySelector('[data-form-step]');
      this.backButton = this.querySelector('[data-back-button]');
      this.activeLabel = this.querySelector('[data-active-category-label]');
      this.messageInput = this.querySelector('[data-message-input]');
      this.platformChips = Array.from(this.querySelectorAll('[data-platform-option]'));
      this.platformsAggregate = this.querySelector('[data-platforms-aggregate]');
      this.multiselect = this.querySelector('[data-multiselect]');
      this.multiselectTrigger = this.querySelector('[data-multiselect-trigger]');
      this.multiselectPanel = this.querySelector('[data-multiselect-panel]');
      this.multiselectDisplay = this.querySelector('[data-multiselect-display]');
      this.platformsPlaceholder = this.multiselectDisplay ? this.multiselectDisplay.textContent.trim() : '';
      this.formEl = this.querySelector('form.gly-partnership__form');
      this.honeypotInput = this.querySelector('input[name="contact[website_url]"]');

      if (!this.radios.length || !this.host) return;

      this.basePlaceholder = this.messageInput ? this.messageInput.getAttribute('placeholder') : '';

      this.onRadioChange = this.onRadioChange.bind(this);
      this.onBackClick = this.onBackClick.bind(this);
      this.onPlatformChange = this.onPlatformChange.bind(this);
      this.onMultiselectToggle = this.onMultiselectToggle.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKey = this.onDocumentKey.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.radios.forEach((radio) => radio.addEventListener('change', this.onRadioChange));
      if (this.backButton) this.backButton.addEventListener('click', this.onBackClick);
      this.platformChips.forEach((cb) => cb.addEventListener('change', this.onPlatformChange));
      if (this.multiselectTrigger) {
        this.multiselectTrigger.addEventListener('click', this.onMultiselectToggle);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKey);
      }
      if (this.formEl) this.formEl.addEventListener('submit', this.onSubmit);
      this.syncPlatforms();

      const checked = this.radios.find((r) => r.checked);
      this.applyTrack(checked ? checked.dataset.trackKey : null, { scroll: false });
    }

    disconnectedCallback() {
      this.radios.forEach((radio) => radio.removeEventListener('change', this.onRadioChange));
      if (this.backButton) this.backButton.removeEventListener('click', this.onBackClick);
      this.platformChips.forEach((cb) => cb.removeEventListener('change', this.onPlatformChange));
      if (this.multiselectTrigger) {
        this.multiselectTrigger.removeEventListener('click', this.onMultiselectToggle);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKey);
      }
      if (this.formEl) this.formEl.removeEventListener('submit', this.onSubmit);
    }

    onSubmit(event) {
      // Honeypot: if a bot filled the off-screen `website_url` field, drop
      // the submission silently so the merchant inbox stays clean.
      if (this.honeypotInput && this.honeypotInput.value.trim() !== '') {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    onPlatformChange() {
      this.syncPlatforms();
    }

    syncPlatforms() {
      const values = this.platformChips.filter((cb) => cb.checked).map((cb) => cb.value);
      if (this.platformsAggregate) {
        this.platformsAggregate.value = values.join(', ');
      }
      if (this.multiselectDisplay) {
        const hasSelection = values.length > 0;
        this.multiselectDisplay.textContent = hasSelection ? values.join(', ') : this.platformsPlaceholder;
        this.multiselectDisplay.classList.toggle('is-placeholder', !hasSelection);
      }
    }

    onMultiselectToggle(event) {
      event.preventDefault();
      const isOpen = this.multiselectTrigger.getAttribute('aria-expanded') === 'true';
      this.setMultiselectOpen(!isOpen);
    }

    onDocumentClick(event) {
      if (!this.multiselect || this.multiselect.contains(event.target)) return;
      this.setMultiselectOpen(false);
    }

    onDocumentKey(event) {
      if (event.key === 'Escape') this.setMultiselectOpen(false);
    }

    setMultiselectOpen(open) {
      if (!this.multiselectTrigger || !this.multiselectPanel) return;
      this.multiselectTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        this.multiselectPanel.removeAttribute('hidden');
      } else {
        this.multiselectPanel.setAttribute('hidden', '');
      }
    }

    onRadioChange(event) {
      this.applyTrack(event.target.dataset.trackKey, { scroll: true });
    }

    onBackClick() {
      this.radios.forEach((r) => { r.checked = false; });
      this.applyTrack(null, { scroll: false });
      const firstCard = this.querySelector('.gly-partnership__cards .gly-partnership__card');
      if (firstCard) firstCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    applyTrack(trackKey, { scroll = false } = {}) {
      const checkedRadio = trackKey ? this.radios.find((r) => r.dataset.trackKey === trackKey) : null;

      if (trackKey) {
        this.host.setAttribute('data-active-track', trackKey);
      } else {
        this.host.removeAttribute('data-active-track');
      }

      if (this.formStep) {
        if (trackKey) {
          this.formStep.removeAttribute('inert');
          this.formStep.removeAttribute('aria-hidden');
        } else {
          this.formStep.setAttribute('inert', '');
          this.formStep.setAttribute('aria-hidden', 'true');
        }
      }

      this.fieldGroups.forEach((group) => {
        const isActive = trackKey && group.dataset.track === trackKey;
        group.querySelectorAll('[data-required-when-active]').forEach((field) => {
          if (isActive) {
            field.setAttribute('required', '');
            field.setAttribute('aria-required', 'true');
          } else {
            field.removeAttribute('required');
            field.removeAttribute('aria-required');
          }
        });
      });

      if (this.activeLabel && checkedRadio) {
        this.activeLabel.textContent = checkedRadio.dataset.trackLabel || '';
      }

      if (this.messageInput) {
        const next = trackKey ? this.messageInput.dataset['msg' + this.capitalize(trackKey)] : '';
        this.messageInput.setAttribute('placeholder', next || this.basePlaceholder);
      }

      if (scroll && trackKey && this.formStep) {
        requestAnimationFrame(() => {
          this.formStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }

  customElements.define('partnership-form', PartnershipForm);
}
